import Button from "@salesforce/design-system-react/components/button";
import IconSettings from "@salesforce/design-system-react/components/icon-settings";
import Modal from "@salesforce/design-system-react/components/modal";
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { CQAppConstant } from "app-constants";
/**
 * Props for the restartPooling modal component.
 *
 * @property setIntervalDuration - Callback that receives a numeric value to update the interval duration used for polling or restart scheduling.
 * @property intervalDurationRef - React.MutableRefObject<number> containing the current interval duration; reading or mutating this ref does not trigger a component re-render.
 *
 * @remarks
 * The unit of the interval value (e.g., milliseconds or seconds) is determined by the component's contract with its callers and should be used consistently.
 */
export interface ICQRestartPollingModalProps {
    setIntervalDuration: (value: number) => void;
    intervalDurationRef: React.MutableRefObject<number>
    setRestartPolling: React.Dispatch<React.SetStateAction<number>>;
}

export function CQRestartPollingModal (props: ICQRestartPollingModalProps) {
    const { t } = useTranslation();
    const [isAlertOpen, setIsAlertOpen] = useState<boolean>(true);


    // Toggles the alert modal's open/closed state.
    function toggleAlertOpen() {
        setIsAlertOpen(prev => !prev);
    }

    /**
     * This function resets the polling interval to a predefined threshold value
     * and closes the alert modal.
     */
    function resumePolling(){
        const { intervalDurationRef, setIntervalDuration, setRestartPolling } = props;

        let intervalDuration  = CQAppConstant.INTERVAL_THRESHOLD; // 10 seconds

        toggleAlertOpen();
        // Update refs and state in a single place for clarity
        intervalDurationRef.current = intervalDuration;
        setIntervalDuration(intervalDuration);
        // Increment so it always changes - a fixed value would repeat and skip the effect.
        setRestartPolling(prev => prev + 1);
    }
    /** 
     * Extracted footer renderer 
     */
    function renderFooter() {
        return [
            <Button
                key="promptBtn"
                label="Yes"
                onClick={resumePolling}
            />
        ];
    }
    /**
     * Renders the modal that prompts the user before restarting polling.
     *
     * Behavior:
     *  - Clicking "Yes" resets:
     *      • intervalDurationRef.current = 10000 (10s)
     *      • setIntervalDuration(10000)
     *  - Prevents closing unless user confirms.
     *
     * @returns {JSX.Element} Modal component asking to restart polling.
    */
    return (
        <>
            <IconSettings iconPath="/assets/icons">
                <Modal
                    disableClose
                    isOpen={isAlertOpen}
                    footer={renderFooter()}
                    prompt="info"
                    size="medium"
                    title={<span>{t("restart_polling")}</span>}
                >
                    <></>
                </Modal>
            </IconSettings>
        </>
    );
}